import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ContentType } from '../content/contracts';
/**
 * Where you were — the read that four screens waited on.
 *
 * `/journey/progress` and `/journey/modules/progress` were POST-only for five
 * rounds, so the product could record that somebody was 12 minutes into an
 * audio and had no way to ask. Home's resume row, `CourseRing`, the Library's
 * `ContinueCard` and "where was I" were all blocked on this one GET.
 */
export const ProgressCourse = z.object({
    modulesTotal: z.number().int().nonnegative(),
    modulesComplete: z.number().int().nonnegative(),
    /**
     * The module to open next — **not** `modulesComplete + 1`.
     *
     * The prototype does that arithmetic and it is wrong the moment anybody skips
     * ahead: complete modules 1, 2 and 4 and the sum names module 3 as "next"
     * while pointing a Resume button at something already watched. The server
     * knows which one is actually outstanding.
     */
    nextModuleId: z.string().uuid().nullable(),
});
export const ProgressItem = z.object({
    contentItemId: z.string().uuid(),
    contentType: ContentType,
    /** The API's own path segment, so a link is not assembled from `contentType`. */
    path: z.string(),
    title: z.string(),
    coverUrl: z.string().nullable(),
    /** Null for anything without a timeline — a reading has no seconds. */
    positionSeconds: z.number().int().nullable(),
    durationSeconds: z.number().int().nullable(),
    completed: z.boolean(),
    completedAt: z.string().nullable(),
    lastActivityAt: z.string(),
    course: ProgressCourse.nullable(),
    /**
     * Which child this session was attributed to — the id ONLY, never a name.
     *
     * Deliberate on the backend's part: Home already reads `/me/crew` to draw
     * the crew board, so resolving the name here would put a second copy of a
     * child's name on a second response, and a child's name is the most private
     * thing this API holds. Join it against the crew read.
     *
     * Null for a session nobody attributed, which is every session recorded
     * before this landed.
     */
    childId: z.string().uuid().nullable().optional(),
});
const ProgressList = z.object({
    items: z.array(ProgressItem),
    /** Rows matching the filter, before paging. */
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    locale: z.string().optional(),
});
/**
 * What you have started.
 *
 * ── Only STARTED things are here, and that is load-bearing ────────────────
 * The list holds nothing a member has not opened, which is why a course ring
 * cannot be drawn from it: a course at `0/4` is absent, not zero, and a ring
 * that read this list would refuse to render until somebody pressed play.
 * `useCourseProgress` is the per-course read for exactly that case.
 *
 * ── An item can drop off while its voyage stop remains ────────────────────
 * The route re-applies audience, entitlement and published-only, so content
 * that goes premium disappears from here while the voyage keeps the stop.
 * Both are right: a record keeps what happened, a button has to work. The
 * underlying row survives and comes back with its resume point if access
 * widens again — so nothing needs caching defensively against it.
 */
export function useProgress(http, options = {}) {
    const { completed, page = 1, pageSize = 20 } = options;
    return useQuery({
        queryKey: ['journey', 'progress', { completed, page, pageSize }],
        queryFn: () => http.get('/journey/progress', {
            schema: ProgressList,
            query: { completed, page, pageSize },
        }),
    });
}
export const CourseModuleProgress = z.object({
    moduleId: z.string().uuid(),
    ordinal: z.number().int(),
    completed: z.boolean(),
    completedAt: z.string().nullable(),
});
export const CourseProgress = z.object({
    modulesTotal: z.number().int().nonnegative(),
    modulesComplete: z.number().int().nonnegative(),
    nextModuleId: z.string().uuid().nullable(),
    contentItemId: z.string().uuid(),
    modules: z.array(CourseModuleProgress),
    completed: z.boolean(),
    completedAt: z.string().nullable(),
    positionSeconds: z.number().int().nullable(),
    lastActivityAt: z.string().nullable(),
});
/**
 * One course, whether or not it has been started.
 *
 * A separate route rather than a filter on the list, deliberately: this is the
 * one that can answer `0/4`. `CourseRing` renders counts, not a percentage,
 * because the design draws the literal "2/4" — deriving one from the other and
 * back loses which it was.
 */
export function useCourseProgress(http, courseId) {
    return useQuery({
        queryKey: ['journey', 'progress', 'course', courseId],
        queryFn: () => http.get(`/journey/progress/courses/${courseId}`, { schema: CourseProgress }),
        enabled: Boolean(courseId),
    });
}
/**
 * The single thing to resume — the row Home and the Library both want.
 *
 * Derived here rather than in two components, and `lastActivityAt` is the only
 * sane ordering: the API returns the list, not an opinion about which row is
 * "current", and "most recently touched, not yet finished" is what a member
 * means by *where was I*.
 */
export function pickResumable(items) {
    const unfinished = items.filter((item) => !item.completed);
    if (unfinished.length === 0)
        return null;
    return unfinished.reduce((latest, item) => item.lastActivityAt > latest.lastActivityAt ? item : latest);
}
//# sourceMappingURL=progress.js.map